/**
 * Event client SDK for AICORES portals.
 * Front-ends call `createEventClient(...)` once per page load and then
 * fire events with `client.fire('prompt_copied', { prompt_id, ... })`.
 *
 * Transport: fire-and-forget POST to the event-logger Worker.
 * In local dev, set VITE_EVENTS_URL to an empty string to log events
 * to the console instead of POSTing them.
 */

export type EventType =
  | 'portal_opened'
  | 'start_here_viewed'
  | 'module_expanded'
  | 'prompt_viewed'
  | 'prompt_copied'
  | 'rating_submitted'
  | 'comment_submitted'
  | 'portal_exited'
  | 'return_visit'
  | 'sequence_skip_detected'
  | 'ome_usage_reported'
  | 'bonus_viewed'
  | 'bonus_copied'
  | 'assessment_started'
  | 'assessment_completed';

export interface EventPayloads {
  portal_opened: { referrer: string; is_first_visit: boolean; token_age_hours: number | null };
  start_here_viewed: { visible_duration_ms: number };
  module_expanded: { module_id: string; module_index: number };
  prompt_viewed: { prompt_id: string; prompt_index: number; module_id: string | null };
  prompt_copied: { prompt_id: string; prompt_index: number; module_id: string | null };
  rating_submitted: { prompt_id: string; rating: 'up' | 'down' };
  comment_submitted: { prompt_id: string; comment_text: string };
  portal_exited: {
    duration_seconds: number;
    prompts_viewed_count: number;
    prompts_copied_count: number;
  };
  return_visit: { days_since_first_visit: number; visit_number: number };
  sequence_skip_detected: { attempted_module: string; attempted_prompt_id: string };
  ome_usage_reported: { answer: 'yes' | 'modified_first' | 'no' };
  bonus_viewed: { bonus_id: string };
  bonus_copied: { bonus_id: string };
  assessment_started: { bonus_id: string };
  assessment_completed: {
    bonus_id: string;
    overall_score: number;
    overall_pct: number;
    pillar_scores: Record<string, { points: number; pct: number }>;
    implementation_order: string[];
    answers: Record<string, number>;
    is_retake: boolean;
  };
}

export interface EventClientConfig {
  endpoint: string;
  token: string;
  productId: string;
  sessionId: string;
  fetchImpl?: typeof fetch;
  debug?: boolean;
}

export interface EventClient {
  fire<T extends EventType>(type: T, payload: EventPayloads[T]): void;
  flush(): Promise<void>;
}

interface QueuedEvent {
  event_type: EventType;
  event_payload_json: string;
  session_id: string;
  product_id: string;
  token: string;
  timestamp: number;
  user_agent: string;
}

export function createEventClient(config: EventClientConfig): EventClient {
  const queue: QueuedEvent[] = [];
  let flushing = false;

  const fetchFn = config.fetchImpl ?? (typeof fetch !== 'undefined' ? fetch : undefined);
  const debug = config.debug ?? false;

  async function flush(): Promise<void> {
    if (flushing || queue.length === 0) return;
    if (!config.endpoint) {
      if (debug) {
        for (const e of queue) {
          console.log('[events]', e.event_type, JSON.parse(e.event_payload_json));
        }
      }
      queue.length = 0;
      return;
    }
    if (!fetchFn) return;
    flushing = true;
    const batch = queue.splice(0, queue.length);
    try {
      await Promise.all(
        batch.map((e) =>
          fetchFn(config.endpoint, {
            method: 'POST',
            headers: {
              'content-type': 'application/json',
              authorization: `Bearer ${e.token}`,
            },
            body: JSON.stringify({
              event_type: e.event_type,
              event_payload_json: e.event_payload_json,
              session_id: e.session_id,
              product_id: e.product_id,
              timestamp: e.timestamp,
              user_agent: e.user_agent,
            }),
            keepalive: true,
          }).catch((err) => {
            if (debug) console.warn('[events] fire failed', e.event_type, err);
          }),
        ),
      );
    } finally {
      flushing = false;
    }
  }

  const userAgent =
    typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent : 'unknown';

  function fire<T extends EventType>(type: T, payload: EventPayloads[T]): void {
    queue.push({
      event_type: type,
      event_payload_json: JSON.stringify(payload),
      session_id: config.sessionId,
      product_id: config.productId,
      token: config.token,
      timestamp: Date.now(),
      user_agent: userAgent,
    });
    void flush();
  }

  return { fire, flush };
}

export function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}
